import { createContext, useContext, useState, useCallback, useMemo } from 'react';

export const UIContext = createContext();

export const UIProvider = ({ children }) => {
  const [hideNav, setHideNav] = useState(false);
  // Sheets stack. Print settings opens over the rounding sheet, and when it
  // closes it must not hand the nav back while the rounding sheet is still up.
  // So sheets count themselves in and out and the nav stays away until the
  // last one has left. The plain boolean above is still there for the screens
  // that own the nav outright.
  const [navHiders, setNavHiders] = useState(0);
  const pushHideNav = useCallback(() => setNavHiders((n) => n + 1), []);
  const popHideNav = useCallback(() => setNavHiders((n) => (n > 0 ? n - 1 : 0)), []);

  // Add action state (for Cards page + Add button)
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [addAction, setAddAction] = useState(null); // 'card' or 'binder'

  // Post modal state
  const [showPostModal, setShowPostModal] = useState(false);
  const [refreshHomeTrigger, setRefreshHomeTrigger] = useState(0);

  // Badge counts for bottom nav (unread messages + unseen requests)
  const [chatBadgeCount, setChatBadgeCount] = useState(0);

  // Trigger home feed refresh
  const triggerHomeRefresh = useCallback(() => setRefreshHomeTrigger(prev => prev + 1), []);

  // Persistent search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Exact card search filters (for "Search This Card" feature)
  const [searchFilters, setSearchFilters] = useState(null);
  // searchFilters: { name, set_name, set_number } - uses AND logic when set

  // Close search completely (X button)
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setSearchFilters(null);
  }, []);

  // Close all modals/sheets (called on navigation)
  const closeAllModals = useCallback(() => {
    setShowAddOptions(false);
    setShowPostModal(false);
  }, []);

  // Open search
  const openSearch = useCallback(() => {
    setSearchOpen(true);
  }, []);

  // Search by name only - "other Suicunes", any set, any number
  const searchCardByName = useCallback((cardInfo) => {
    setSearchResults([]);
    setSearchQuery(cardInfo.name || '');
    setSearchFilters({ name: cardInfo.name });
    setSearchOpen(true);
  }, []);

  // Search for exact card (uses AND logic)
  const searchExactCard = useCallback((cardInfo) => {
    setSearchResults([]);
    setSearchQuery(cardInfo.name || '');
    setSearchFilters({
      name: cardInfo.name,
      set_name: cardInfo.set_name,
      set_number: cardInfo.set_number,
    });
    setSearchOpen(true);
  }, []);

  // A fresh object here re-rendered every screen reading useUI on any change
  // to any field, including ones they do not use.
  const value = useMemo(() => ({
    hideNav: hideNav || navHiders > 0,
    setHideNav,
    pushHideNav,
    popHideNav,
    showAddOptions,
    setShowAddOptions,
    addAction,
    setAddAction,
    showPostModal,
    setShowPostModal,
    refreshHomeTrigger,
    triggerHomeRefresh,
    searchOpen,
    searchQuery,
    searchResults,
    setSearchQuery,
    setSearchResults,
    openSearch,
    closeSearch,
    searchFilters,
    setSearchFilters,
    searchExactCard,
    searchCardByName,
    chatBadgeCount,
    setChatBadgeCount,
    closeAllModals,
  }), [
    hideNav, navHiders, setHideNav, pushHideNav, popHideNav, showAddOptions, setShowAddOptions, addAction, setAddAction, showPostModal, setShowPostModal, refreshHomeTrigger, triggerHomeRefresh, searchOpen, searchQuery, searchResults, setSearchQuery, setSearchResults, openSearch, closeSearch, searchFilters, setSearchFilters, searchExactCard, searchCardByName, chatBadgeCount, setChatBadgeCount, closeAllModals,
  ]);

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};
